/**
 * DocumentItem - Shared document card component
 * Used by DocumentsView and DocumentGallery
 *
 * Supports two usage patterns:
 * 1. Default: Renders with standard Eye/Download/Delete actions (DocumentsView style)
 * 2. Custom: Renders with actionsSlot for custom actions (DocumentGallery style)
 */

import React from 'react';
import {
  FileText, Image, File, Download, Trash2, Clock, AlertTriangle,
  Loader2, Calendar, Archive, CheckCircle, XCircle, ClipboardCheck, Eye
} from 'lucide-react';
import { useFormat, formatDate as formatDateI18n } from '../../i18n/format.js';
import i18n from '../../i18n/index.js';

// Motifs d'archivage renvoyés par getArchiveStatus() (config/docstore.js) → clé i18n
const ARCHIVE_REASON_KEYS = {
  'Supprime': 'deleted',
  'Archive': 'archived',
  'Expire': 'expired',
  'Ancienne version': 'oldVersion'
};

// Get file icon based on MIME type
function getFileIcon(mimeType, className = 'w-5 h-5') {
  if (mimeType?.startsWith('image/')) {
    return <Image className={`${className} text-green-600`} />;
  }
  if (mimeType === 'application/pdf') {
    return <FileText className={`${className} text-red-600`} />;
  }
  return <File className={`${className} text-blue-600`} />;
}

// Calculate expiry status
function getExpiryStatus(validTo) {
  if (!validTo) return null;
  const today = new Date();
  const expiry = new Date(validTo);
  const daysUntil = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

  if (daysUntil < 0) return { status: 'expired', days: Math.abs(daysUntil) };
  if (daysUntil <= 30) return { status: 'critical', days: daysUntil };
  if (daysUntil <= 90) return { status: 'warning', days: daysUntil };
  return { status: 'ok', days: daysUntil };
}

// Format date
function formatDate(dateStr) {
  if (!dateStr) return '-';
  return formatDateI18n(dateStr);
}

// Format file size
function formatSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Get document type label
function getTypeLabel(documentType) {
  if (!documentType) return i18n.t('documents.item.defaultType');
  // Simple label extraction from document type code
  const parts = documentType.split('_');
  if (parts.length > 1) {
    return parts.slice(1).join(' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
  }
  return documentType;
}

/**
 * DocumentItem Component
 *
 * @param {Object} doc - Document object
 * @param {boolean} isArchived - Whether document is archived
 * @param {Function} onView - Callback when viewing document
 * @param {Function} onDownload - Callback when downloading document
 * @param {Function} onDelete - Callback when deleting document
 * @param {boolean} isDeleting - Whether delete is in progress
 * @param {ReactNode} actionsSlot - Custom actions to render instead of default
 * @param {string} actionsPosition - 'column' (default) or 'inline' (inside card)
 * @param {string} typeLabel - Optional override for document type label
 * @param {string} archiveReasonKey - Key for archive reason in doc object ('_archiveReason' or 'archiveReason')
 * @param {Function} onCardClick - Custom card click handler (overrides default behavior)
 * @param {Function} onValidate - Callback for validation (renders clickable button for pending docs)
 */
export default function DocumentItem({
  doc,
  isArchived = false,
  onView,
  onDownload,
  onDelete,
  isDeleting = false,
  actionsSlot,
  actionsPosition = 'column',
  typeLabel,
  archiveReasonKey = '_archiveReason',
  onCardClick: customCardClick,
  onValidate
}) {
  const { t } = useFormat();
  const expiry = getExpiryStatus(doc.valid_to);
  const displayTypeLabel = typeLabel || getTypeLabel(doc.document_type);
  const canPreview = doc.mime_type === 'application/pdf' || doc.mime_type?.startsWith('image/');
  const archiveReason = doc[archiveReasonKey];

  const handleCardClick = (e) => {
    e.stopPropagation();
    if (customCardClick) {
      customCardClick(doc, e);
    } else if (canPreview && onView) {
      onView(doc);
    } else if (onDownload) {
      onDownload(doc);
    }
  };

  const handleEyeClick = (e) => {
    e.stopPropagation();
    onView?.(doc);
  };

  const handleDownloadClick = (e) => {
    e.stopPropagation();
    onDownload?.(doc);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onDelete?.(doc);
  };

  // Default actions column (DocumentsView style)
  const defaultActions = (
    <div className="flex flex-col items-center gap-1">
      {canPreview && onView && (
        <button
          type="button"
          onClick={handleEyeClick}
          className="p-1.5 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded transition-colors"
          title={t('documents.item.view')}
        >
          <Eye className="w-4 h-4" />
        </button>
      )}
      {onDownload && (
        <button
          type="button"
          onClick={handleDownloadClick}
          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
          title={t('common.actions.download')}
        >
          <Download className="w-4 h-4" />
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={handleDeleteClick}
          disabled={isDeleting}
          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
          title={t('common.actions.delete')}
        >
          {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </button>
      )}
    </div>
  );

  // Content block (icon, filename, metadata, badges)
  const contentBlock = (
    <>
      <div className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
        isArchived ? 'bg-gray-100' : 'bg-gray-50'
      }`}>
        {getFileIcon(doc.mime_type, 'w-5 h-5')}
      </div>

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${
          isArchived ? 'text-gray-500' : 'text-gray-900'
        }`}>
          {doc.original_filename || doc.filename}
        </p>
        <div className="flex items-center flex-wrap gap-2 mt-1">
          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
            {displayTypeLabel}
          </span>
          <span className="text-xs text-gray-500">{formatSize(doc.file_size)}</span>
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <Calendar className="w-3 h-3" />
            {formatDate(doc.created_at)}
          </span>
        </div>

        {/* Archive reason badge */}
        {isArchived && archiveReason && (
          <div className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
            <Archive className="w-3 h-3" />
            {t(`documents.archiveReasons.${ARCHIVE_REASON_KEYS[archiveReason] || archiveReason}`, { defaultValue: archiveReason })}
          </div>
        )}

        {/* Validation status badge - clickable for pending when onValidate provided */}
        {!isArchived && doc.validation_status && (
          doc.validation_status === 'pending' && onValidate ? (
            // Clickable badge for pending validation - distinct style with border to show it's clickable
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onValidate(doc);
              }}
              className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 hover:text-amber-800 border-2 border-amber-400 hover:border-amber-500 transition-colors cursor-pointer shadow-sm"
              title={t('documents.item.clickToValidate')}
            >
              <ClipboardCheck className="w-3 h-3" />
              {t('documents.validationStatus.pending')}
            </button>
          ) : (
            // Static badge for other statuses
            <div className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${
              doc.validation_status === 'pending' ? 'bg-amber-100 text-amber-700' :
              doc.validation_status === 'validated' ? 'bg-green-100 text-green-700' :
              doc.validation_status === 'rejected' ? 'bg-red-100 text-red-700' :
              doc.validation_status === 'cannot_provide' ? 'bg-yellow-100 text-yellow-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {doc.validation_status === 'pending' && <><ClipboardCheck className="w-3 h-3" /> {t('documents.validationStatus.pending')}</>}
              {doc.validation_status === 'validated' && <><CheckCircle className="w-3 h-3" /> {t('documents.validationStatus.validated')}</>}
              {doc.validation_status === 'rejected' && <><XCircle className="w-3 h-3" /> {t('documents.validationStatus.rejected')}</>}
              {doc.validation_status === 'cannot_provide' && <><AlertTriangle className="w-3 h-3" /> {t('documents.validationStatus.cannotProvide')}</>}
            </div>
          )
        )}

        {/* Expiry warning for active docs */}
        {!isArchived && expiry && expiry.status !== 'ok' && (
          <div className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${
            expiry.status === 'expired' ? 'bg-red-100 text-red-700' :
            expiry.status === 'critical' ? 'bg-orange-100 text-orange-700' :
            'bg-yellow-100 text-yellow-700'
          }`}>
            <Clock className="w-3 h-3" />
            {t('documents.expiry.days', { days: expiry.days })}
          </div>
        )}
      </div>
    </>
  );

  // Actions to render
  const actionsToRender = actionsSlot || defaultActions;

  // Inline layout: actions inside the card
  if (actionsPosition === 'inline') {
    return (
      <div
        className={`border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer group ${
          isArchived ? 'bg-gray-50 border-gray-200 opacity-75' : 'bg-white border-gray-200 hover:border-blue-300'
        }`}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleCardClick(e)}
      >
        <div className="flex items-start gap-3">
          {contentBlock}
          {actionsToRender}
        </div>
      </div>
    );
  }

  // Column layout (default): actions in separate column outside card
  return (
    <div className="flex items-center gap-2 group">
      <div
        className={`flex-1 border rounded-lg p-3 hover:shadow-md transition-all cursor-pointer ${
          isArchived ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200 hover:border-blue-300'
        }`}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleCardClick(e)}
      >
        <div className="flex items-start gap-3">
          {contentBlock}
        </div>
      </div>
      {actionsToRender}
    </div>
  );
}

// Re-export utility functions for use by consumers
export { getFileIcon, getExpiryStatus, formatDate, formatSize, getTypeLabel };
